/**
 * Loads the previews of the cameras that a store sees come online. One fetcher serves one store,
 * so `abort` at an organization switch stops the queue of the organization that goes.
 *
 * At most PREFETCH_CONCURRENCY previews load at a time. The server can long-poll a preview
 * request for about 10 seconds while the camera has no frame, and each request holds one of the
 * 6 connections that the browser allows for an origin. A request for each camera at the same
 * time thus stops each other call of the page (event images, the server list) for that time.
 * When the server answers at once without a frame, the limit can go.
 */

import type { StoredPreview } from '@/types/devices';

import { isFreshPreview } from './cameraPreviews';

/** A camera to load, with the media_server that relays its picture. */
type PrefetchTarget = {
    id: string;
    serverId?: string;
};

interface Deps {
    fetchPreview: (
        id: string, serverId: string | undefined, signal: AbortSignal,
    ) => Promise<Blob | null>;
    readPreview: (id: string) => StoredPreview | null;
    write: (id: string, dataUrl: string) => void;
}

interface PreviewFetcher {
    prefetch(targets: readonly PrefetchTarget[]): void;
    abort(): void;
}

export const PREFETCH_CONCURRENCY = 2;

/** The blob as a `data:` URL, or null when the read fails. */
function readDataUrl(blob: Blob): Promise<string | null> {
    return new Promise((resolve): void => {
        const reader = new FileReader();
        reader.onload = (): void => {
            resolve(typeof reader.result === 'string' ? reader.result : null);
        };
        reader.onerror = (): void => resolve(null);
        reader.readAsDataURL(blob);
    });
}

export function createPreviewFetcher({
    fetchPreview, readPreview, write,
}: Deps): PreviewFetcher {
    function isFresh(id: string): boolean {
        return isFreshPreview(readPreview(id), Date.now());
    }

    async function load({ id, serverId }: PrefetchTarget): Promise<void> {
        let blob: Blob | null;
        try {
            blob = await fetchPreview(id, serverId, controller.signal);
        } catch {
            // The camera keeps the preview it had.
            return;
        }
        if (blob === null || blob.size === 0) {
            return;
        }
        const dataUrl = await readDataUrl(blob);
        if (dataUrl !== null && !controller.signal.aborted) {
            write(id, dataUrl);
        }
    }

    function pump(): void {
        while (activeCount < PREFETCH_CONCURRENCY && !controller.signal.aborted) {
            const target = queue.shift();
            if (target === undefined) {
                return;
            }
            // A preview can arrive from a tile while the camera waits.
            if (isFresh(target.id)) {
                pendingIds.delete(target.id);
                continue;
            }
            activeCount += 1;
            load(target).finally((): void => {
                activeCount -= 1;
                pendingIds.delete(target.id);
                pump();
            });
        }
    }

    const controller = new AbortController();
    const queue: PrefetchTarget[] = [];
    /** The cameras in the queue or in flight, so a camera queued twice is fetched once. */
    const pendingIds = new Set<string>();
    let activeCount = 0;

    return {
        prefetch(targets: readonly PrefetchTarget[]): void {
            for (const target of targets) {
                if (pendingIds.has(target.id) || isFresh(target.id)) {
                    continue;
                }
                pendingIds.add(target.id);
                queue.push(target);
            }
            pump();
        },
        abort(): void {
            controller.abort();
            queue.length = 0;
            pendingIds.clear();
        },
    };
}
